import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Phone, Mail, MapPin, Star, ChevronDown, Menu, X, ChevronLeft, ChevronRight, Send, AlertCircle, CheckCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';

// Memoized icons for better performance
const MemoizedPhone = memo(Phone);
const MemoizedMail = memo(Mail);
const MemoizedMapPin = memo(MapPin);
const MemoizedStar = memo(Star);
const MemoizedChevronDown = memo(ChevronDown);
const MemoizedMenu = memo(Menu);
const MemoizedX = memo(X);
const MemoizedChevronLeft = memo(ChevronLeft);
const MemoizedChevronRight = memo(ChevronRight);
const MemoizedSend = memo(Send);
const MemoizedAlertCircle = memo(AlertCircle);
const MemoizedCheckCircle = memo(CheckCircle);

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [aboutSliderIndex, setAboutSliderIndex] = useState(0);
  const [isSliderPaused, setIsSliderPaused] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [currentServiceType, setCurrentServiceType] = useState('');
  const [currentServiceImageIndex, setCurrentServiceImageIndex] = useState(0);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  // Contact Form State
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    honeypot: '' // Hidden field for bot detection
  });
  
  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', 'rate_limited'
  const [submitMessage, setSubmitMessage] = useState('');
  
  // Rate limiting
  const [lastSubmissionTime, setLastSubmissionTime] = useState(0);
  const [submissionCount, setSubmissionCount] = useState(0);
  const RATE_LIMIT_WINDOW = 5 * 60 * 1000; // 5 minutes
  const MAX_SUBMISSIONS = 3;

  // Portfolio categories with dynamic folder structure
  const [portfolioCategories, setPortfolioCategories] = useState([
    {
      folder: 'jednohroby',
      title: 'Jednohroby',
      description: 'Individuálne riešenia jednohrobov s dôrazom na osobný charakter a kvalitné spracovanie. Používame kvalitný prírodný kameň s dlhodobou odolnosťou proti poveternostným vplyvom a precízne remeselné technikou.',
      images: [
        { src: '/jednohroby/jednohroby-01.webp', alt: 'Jednohroby 1', title: 'Jednohroby - Ukážka 1' },
        { src: '/jednohroby/jednohroby-02.webp', alt: 'Jednohroby 2', title: 'Jednohroby - Ukážka 2' }
      ]
    },
    {
      folder: 'dvojhroby',
      title: 'Dvojhroby', 
      description: 'Rozsiahlejšie riešenia dvojhrobov s možnosťou kombinovania rôznych materiálov a dizajnových prvkov. Vytvárame pamätníky, ktoré zdôrazňujú spoločnú históriu a pripomínajú spoločne prežité chvíle.',
      images: [
        { src: '/dvojhroby/dvojhroby-01.webp', alt: 'Dvojhroby 1', title: 'Dvojhroby - Ukážka 1' },
        { src: '/dvojhroby/dvojhroby-02.webp', alt: 'Dvojhroby 2', title: 'Dvojhroby - Ukážka 2' }
      ]
    },
    {
      folder: 'gravirovania',
      title: 'Gravírovania',
      description: 'Precízne gravírovanie nápisov, ornamentov a symbolov pomocí tradičných i moderných techník. Každý nápis vytvárame s najvyššou pozornosťou k detailom pre trvanlivý a dôstojný výsledok.',
      images: [
        { src: '/gravirovania/gravirovania-01.webp', alt: 'Gravírovania 1', title: 'Gravírovania - Ukážka 1' },
        { src: '/gravirovania/gravirovania-02.webp', alt: 'Gravírovania 2', title: 'Gravírovania - Ukážka 2' }
      ]
    },
    {
      folder: 'dekorativne',
      title: 'Hroby s dekoratívnym pomníkom',
      description: 'Umelecké pamätníky kombinujúce sochárske prvky s tradičnou kamenárskou prácou. Vytvárame jedinečné dekoratívne pomníky s anjelmi, reliéfmi a inými výtvarnými prvkami podľa vašich predstáv.',
      images: [
        { src: '/dekorativne/dekorativne-01.webp', alt: 'Dekoratívne 1', title: 'Dekoratívne - Ukážka 1' },
        { src: '/dekorativne/dekorativne-02.webp', alt: 'Dekoratívne 2', title: 'Dekoratívne - Ukážka 2' }
      ]
    }
  ]);

  // Initialize EmailJS
  useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    if (publicKey && publicKey !== 'YOUR_PUBLIC_KEY') {
      emailjs.init(publicKey);
    }
  }, []);

  // Load portfolio images dynamically on component mount
  useEffect(() => {
    const loadPortfolioImages = async () => {
      const categoryConfigs = [
        { folder: 'jednohroby', title: 'Jednohroby', description: 'Individuálne riešenia jednohrobov s dôrazom na osobný charakter a kvalitné spracovanie. Používame kvalitný prírodný kameň s dlhodobou odolnosťou proti poveternostným vplyvom a precízne remeselné technikou.' },
        { folder: 'dvojhroby', title: 'Dvojhroby', description: 'Rozsiahlejšie riešenia dvojhrobov s možnosťou kombinovania rôznych materiálov a dizajnových prvkov. Vytvárame pamätníky, ktoré zdôrazňujú spoločnú históriu a pripomínajú spoločne prežité chvíle.' },
        { folder: 'gravirovania', title: 'Gravírovania', description: 'Precízne gravírovanie nápisov, ornamentov a symbolov pomocí tradičných i moderných techník. Každý nápis vytvárame s najvyššou pozornosťou k detailom pre trvanlivý a dôstojný výsledok.' },
        { folder: 'dekorativne', title: 'Hroby s dekoratívnym pomníkom', description: 'Umelecké pamätníky kombinujúce sochárske prvky s tradičnou kamenárskou prácou. Vytvárame jedinečné dekoratívne pomníky s anjelmi, reliéfmi a inými výtvarnými prvkami podľa vašich predstáv.' }
      ];

      const updatedCategories = [];

      for (const category of categoryConfigs) {
        const images = [];
        
        // Try to load up to 50 images per category
        for (let imageIndex = 1; imageIndex <= 50; imageIndex++) {
          const imagePath = `/${category.folder}/${category.folder}-${imageIndex.toString().padStart(2, '0')}.webp`;
          
          try {
            // Create a new image to test if it loads
            const img = new Image();
            const loadPromise = new Promise((resolve, reject) => {
              img.onload = () => resolve(true);
              img.onerror = () => reject(false);
              img.src = imagePath;
            });

            await loadPromise;
            
            // If image loads successfully, add it to array
            images.push({
              src: imagePath,
              alt: `${category.title} ${imageIndex}`,
              title: `${category.title} - Ukážka ${imageIndex}`
            });
          } catch (error) {
            // Image failed to load, stop trying for this category
            break;
          }
        }

        // Add category with loaded images
        updatedCategories.push({
          ...category,
          images: images
        });
      }

      // Update state with dynamically loaded categories
      if (updatedCategories.length > 0) {
        setPortfolioCategories(updatedCategories);
      }
    };

    loadPortfolioImages();
  }, []);

  // Service modal images - Fixed dynamic loading
  const [serviceModalImages, setServiceModalImages] = useState({
    jednohroby: [
      { src: "/sluzby-jednohroby-modal/jednohroby-01.webp", alt: "Jednohroby 1", title: "Jednohroby - Ukážka 1" },
      { src: "/sluzby-jednohroby-modal/jednohroby-02.webp", alt: "Jednohroby 2", title: "Jednohroby - Ukážka 2" },
      { src: "/sluzby-jednohroby-modal/jednohroby-03.webp", alt: "Jednohroby 3", title: "Jednohroby - Ukážka 3" },
      { src: "/sluzby-jednohroby-modal/jednohroby-04.webp", alt: "Jednohroby 4", title: "Jednohroby - Ukážka 4" }
    ],
    dvojhroby: [
      { src: "/sluzby-dvojhroby-modal/dvojhroby-01.webp", alt: "Dvojhroby 1", title: "Dvojhroby - Ukážka 1" },
      { src: "/sluzby-dvojhroby-modal/dvojhroby-02.webp", alt: "Dvojhroby 2", title: "Dvojhroby - Ukážka 2" },
      { src: "/sluzby-dvojhroby-modal/dvojhroby-03.webp", alt: "Dvojhroby 3", title: "Dvojhroby - Ukážka 3" }
    ]
  });

  // Load service modal images dynamically on component mount
  useEffect(() => {
    const loadServiceImages = async () => {
      const serviceTypes = [
        { type: 'jednohroby', maxImages: 50 },
        { type: 'dvojhroby', maxImages: 50 }
      ];
      
      const newServiceImages = {};

      for (const { type, maxImages } of serviceTypes) {
        const images = [];
        
        for (let imageIndex = 1; imageIndex <= maxImages; imageIndex++) {
          const imagePath = `/sluzby-${type}-modal/${type}-${imageIndex.toString().padStart(2, '0')}.webp`;
          
          try {
            // Create a new image to test if it loads
            const img = new Image();
            const loadPromise = new Promise((resolve, reject) => {
              img.onload = () => resolve(true);
              img.onerror = () => reject(false);
              img.src = imagePath;
            });

            await loadPromise;
            
            // If image loads successfully, add it to array
            images.push({
              src: imagePath,
              alt: `${type} ${imageIndex}`,
              title: `${type === 'jednohroby' ? 'Jednohroby' : 'Dvojhroby'} - Ukážka ${imageIndex}`
            });
          } catch (error) {
            // Image failed to load, stop trying for this service type
            break;
          }
        }

        newServiceImages[type] = images;
      }

      // Only update if we found images
      if (newServiceImages.jednohroby.length > 0 || newServiceImages.dvojhroby.length > 0) {
        setServiceModalImages(newServiceImages);
      }
    };

    loadServiceImages();
  }, []);
  const aboutSliderImages = [
    {
      src: "/slider-onas/slider-01.webp",
      alt: "Kamenárske remeslo",
      title: "Tradičné Remeselné Postupy"
    },
    {
      src: "/slider-onas/slider-02.webp", 
      alt: "Gravírovanie",
      title: "Presné Gravírovanie"
    },
    {
      src: "/slider-onas/slider-03.webp",
      alt: "Údržba hrobov",
      title: "Profesionálna Údržba"
    },
    {
      src: "/slider-onas/slider-04.webp",
      alt: "Hrobové pamätníky", 
      title: "Kvalitné Pamätníky"
    }
  ];

  // Auto-advance about slider
  useEffect(() => {
    if (isSliderPaused) return;
    
    const interval = setInterval(() => {
      setAboutSliderIndex((prev) => 
        prev === aboutSliderImages.length - 1 ? 0 : prev + 1
      );
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [isSliderPaused, aboutSliderImages.length]);




  // Optimized service modal functions with useCallback
  const openServiceModal = useCallback((serviceType) => {
    setCurrentServiceType(serviceType);
    setCurrentServiceImageIndex(0);
    setIsServiceModalOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeServiceModal = useCallback(() => {
    setIsServiceModalOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  const goToPreviousServiceImage = useCallback(() => {
    const images = serviceModalImages[currentServiceType] || [];
    setCurrentServiceImageIndex((prev) => 
      prev === 0 ? images.length - 1 : prev - 1
    );
  }, [serviceModalImages, currentServiceType]);

  const goToNextServiceImage = useCallback(() => {
    const images = serviceModalImages[currentServiceType] || [];
    setCurrentServiceImageIndex((prev) => 
      prev === images.length - 1 ? 0 : prev + 1
    );
  }, [serviceModalImages, currentServiceType]);
  // Optimized slider navigation functions with useCallback
  const goToPreviousSlide = useCallback(() => {
    setIsSliderPaused(true);
    setAboutSliderIndex(prev => 
      prev === 0 ? aboutSliderImages.length - 1 : prev - 1
    );
    // Resume auto-play after 5 seconds
    setTimeout(() => setIsSliderPaused(false), 5000);
  }, [aboutSliderImages.length]);

  const goToNextSlide = useCallback(() => {
    setIsSliderPaused(true);
    setAboutSliderIndex(prev => 
      prev === aboutSliderImages.length - 1 ? 0 : prev + 1
    );
    // Resume auto-play after 5 seconds
    setTimeout(() => setIsSliderPaused(false), 5000);
  }, [aboutSliderImages.length]);

  const goToSlide = useCallback((index) => {
    setIsSliderPaused(true);
    setAboutSliderIndex(index);
    // Resume auto-play after 5 seconds
    setTimeout(() => setIsSliderPaused(false), 5000);
  }, []);

  // Contact form handlers with useCallback for performance
  const handleContactFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const validateForm = useCallback(() => {
    const { name, email, message, honeypot } = contactForm;
    
    // Bot detection - if honeypot is filled, it's a bot
    if (honeypot.length > 0) {
      return { isValid: false, message: 'Bot detected.' };
    }
    
    // Basic validation
    if (!name.trim() || name.trim().length < 2) {
      return { isValid: false, message: 'Meno musí mať aspoň 2 znaky.' };
    }
    
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return { isValid: false, message: 'Prosím zadajte platný email.' };
    }
    
    if (!message.trim() || message.trim().length < 10) {
      return { isValid: false, message: 'Správa musí mať aspoň 10 znakov.' };
    }
    
    if (message.trim().length > 1000) {
      return { isValid: false, message: 'Správa je príliš dlhá (max 1000 znakov).' };
    }
    
    return { isValid: true, message: '' };
  }, [contactForm]);

  const checkRateLimit = useCallback(() => {
    const now = Date.now();
    const timeSinceLastSubmission = now - lastSubmissionTime;
    
    // Reset counter if window expired
    if (timeSinceLastSubmission > RATE_LIMIT_WINDOW) {
      setSubmissionCount(0);
      return { allowed: true, message: '' };
    }
    
    if (submissionCount >= MAX_SUBMISSIONS) {
      const remainingTime = Math.ceil((RATE_LIMIT_WINDOW - timeSinceLastSubmission) / 60000);
      return { 
        allowed: false, 
        message: `Príliš veľa pokusov. Skúste znovu za ${remainingTime} minút.` 
      };
    }
    
    return { allowed: true, message: '' };
  }, [lastSubmissionTime, submissionCount, RATE_LIMIT_WINDOW, MAX_SUBMISSIONS]);

  const submitContactForm = useCallback(async (e) => {
    e.preventDefault();
    
    // Validate form
    const validation = validateForm();
    if (!validation.isValid) {
      setSubmitStatus('error');
      setSubmitMessage(validation.message);
      return;
    }
    
    // Check rate limiting
    const rateCheck = checkRateLimit();
    if (!rateCheck.allowed) {
      setSubmitStatus('rate_limited');
      setSubmitMessage(rateCheck.message);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage('');

    try {
      // Prepare email data
      const templateParams = {
        to_email: 'samm1254@gmail.com',
        from_name: contactForm.name.trim(),
        from_email: contactForm.email.trim(),
        phone: contactForm.phone.trim() || 'Neposkytnuté',
        message: contactForm.message.trim(),
        timestamp: new Date().toLocaleString('sk-SK'),
        website: window.location.origin
      };

      // Send email via EmailJS
      const response = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      if (response.status === 200) {
        setSubmitStatus('success');
        setSubmitMessage('Správa bola úspešne odoslaná! Ozveme sa vám čoskoro.');
        
        // Update rate limiting
        const now = Date.now();
        setLastSubmissionTime(now);
        setSubmissionCount(prev => prev + 1);
        
        // Reset form
        setContactForm({
          name: '',
          email: '',
          phone: '',
          message: '',
          honeypot: ''
        });
      }
    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitStatus('error');
      
      if (error.status === 429) {
        setSubmitMessage('Server je preťažený. Skúste to znovu za chvíľu.');
      } else if (error.status === 400) {
        setSubmitMessage('Neplatné dáta formulára. Skúste to znovu.');
      } else {
        setSubmitMessage('Nepodarilo sa odoslať správu. Skúste to znovu alebo nás kontaktujte telefonicky.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [contactForm, validateForm, checkRateLimit, lastSubmissionTime, submissionCount]);

  // Auto-clear status messages
  useEffect(() => {
    if (submitStatus) {
      const timer = setTimeout(() => {
        setSubmitStatus(null);
        setSubmitMessage('');
      }, 8000); // Clear after 8 seconds
      
      return () => clearTimeout(timer);
    }
  }, [submitStatus]);

  // Optimized portfolio modal functions with useCallback
  const openModal = useCallback((categoryIndex) => {
    setCurrentCategoryIndex(categoryIndex);
    setCurrentImageIndex(0); // Start with first image in category
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  // Get current category images for modal - Memoized
  const currentCategoryImages = useMemo(() => 
    portfolioCategories[currentCategoryIndex]?.images || [], 
    [portfolioCategories, currentCategoryIndex]
  );
  
  // Optimized navigation functions with useCallback
  const goToPrevious = useCallback(() => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? currentCategoryImages.length - 1 : prev - 1
    );
  }, [currentCategoryImages.length]);

  const goToNext = useCallback(() => {
    setCurrentImageIndex((prev) => 
      prev === currentCategoryImages.length - 1 ? 0 : prev + 1
    );
  }, [currentCategoryImages.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isModalOpen && !isServiceModalOpen) {
        switch (e.key) {
          case 'Escape':
            closeModal();
            break;
          case 'ArrowLeft':
            goToPrevious();
            break;
          case 'ArrowRight':
            goToNext();
            break;
          default:
            break;
        }
      } else if (isServiceModalOpen) {
        switch (e.key) {
          case 'Escape':
            closeServiceModal();
            break;
          case 'ArrowLeft':
            goToPreviousServiceImage();
            break;
          case 'ArrowRight':
            goToNextServiceImage();
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, isServiceModalOpen]);

  const services = [
    {
      title: "Jednohroby a Dvojhroby",
      description: "Profesionálne zhotovenie hrobov s dôrazom na kvalitu materiálu a remeselné spracovania. Každý hrob je jedinečný a vytvorený podľa vašich predstáv.",
      image: "/sluzby-jednohroby/sluzba.webp",
      features: ["Kvalitný prírodný kameň", "Individuálny dizajn", "Presné spracovanie", "Dlhodobá záruka"]
    },
    {
      title: "Brúsenie a Čistenie Hrobov",
      description: "Obnovenie pôvodnej krásy vašich hrobov pomocou profesionálnych techník čistenia a brúsenia. Vrátíme kameňu jeho prirodzenú eleganciu.",
      image: "/sluzby-brusenie/sluzba.webp",
      features: ["Šetrné čistiace postupy", "Profesionálne nástroje", "Ochrana materiálu", "Dlhodobý efekt"]
    },
    {
      title: "Obnova Písma - Gravírovanie",
      description: "Obnovenie a nové gravírovanie nápisov na hroboch. Používame tradičné i moderné techniky pre dokonalý výsledok s dôrazom na detaily.",
      image: "/sluzby-gravirovania/sluzba.webp",
      features: ["Presné gravírovanie", "Rôzne štýly písma", "Obnova starých nápisov", "Trvanlivé riešenia"]
    },
    {
      title: "Vyrovnávanie Klesnutých Hrobov",
      description: "Profesionálne vyrovnanie a stabilizácia klesnutých hrobov. Zabezpečujeme dlhodobú stabilitu a estetický vzhľad hrobového miesta.",
      image: "/sluzby-vyrovnavanie/sluzba.webp",
      features: ["Stabilné základy", "Presné vyrovnanie", "Preventívne opatrenia", "Komplexné riešenie"]
    }
  ];

  // Optimized scroll to section with useCallback
  const scrollToSection = useCallback((sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <motion.nav 
        className="fixed top-0 w-full bg-white/90 backdrop-blur-md z-50 shadow-lg"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <motion.div 
              className="text-2xl font-bold text-gray-800"
              whileHover={{ scale: 1.05 }}
            >
              Kamenárstvo Studený Smix
            </motion.div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex space-x-8">
              {['Domov', 'Služby', 'Portfolio', 'O nás', 'Kontakt'].map((item, index) => (
                <motion.button
                  key={item}
                  className="text-gray-700 hover:text-amber-600 font-medium transition-colors duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  layout={false}
                  onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))}
                >
                  {item}
                </motion.button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div 
            className="md:hidden bg-white border-t"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="px-4 py-2 space-y-2">
              {['Domov', 'Služby', 'Portfolio', 'O nás', 'Kontakt'].map((item) => (
                <button
                  key={item}
                  className="block w-full text-left py-2 text-gray-700 hover:text-amber-600"
                  onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))}
                >
                  {item}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </motion.nav>

      {/* Hero Section */}
      <section id="domov" className="h-screen relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.4)), url("/pozadia/background.webp")`
          }}
        />
        <motion.div 
          className="relative z-10 h-full flex items-center justify-center text-center text-white px-4"
          style={{ opacity }}
        >
          <div className="max-w-4xl">
            <motion.h1 
              className="text-5xl md:text-7xl font-bold mb-6 leading-tight"
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              layout={false}
            >
              Tradícia v Kameni
            </motion.h1>
            <motion.p 
              className="text-xl md:text-2xl mb-8 font-light"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              layout={false}
            >
              Vitajte na stránke <b>Kamenárstva SMIX v Nitre</b>.<br />
              Profesionálne služby pre dôstojné pripomínanie vašich blízkych.
            </motion.p>
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              layout={false}
            >
              <button 
                onClick={() => scrollToSection('služby')}
                className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Naše Služby
              </button>
              <button 
                onClick={() => scrollToSection('kontakt')}
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105"
              >
                Kontaktujte Nás
              </button>
            </motion.div>
          </div>
        </motion.div>
        
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown size={32} />
        </motion.div>
      </section>

      {/* Services Section */}
      <section id="služby" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            layout={false}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Naše Služby</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Poskytujeme komplexné kamenárske služby s dôrazom na kvalitu, tradíciu a rešpekt k vašim potrebám.
              Už niekoľko rokov sa venujeme výrobe a úpravám pomníkov a hrobov z kvalitného prírodného kameňa. 
            </p>
          </motion.div>

          {/* Price Information Section */}
          <motion.div 
            className="mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            layout={false}
          >
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-100">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Cenník Našich Služieb</h3>
                <p className="text-lg text-gray-600">Transparentné ceny pre vaše rozhodovanie</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                <motion.div 
                  className="bg-gradient-to-br from-amber-50 to-amber-100 p-8 rounded-2xl border-2 border-amber-200 text-center"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">1</span>
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">Jednohrob + pomník</h4>
                  <div className="text-4xl font-bold text-amber-700 mb-2">od 1600 €</div>
                  <p className="text-amber-800 font-semibold mb-2">vrátane práce</p>
                  <p className="text-sm text-gray-600">Kompletné riešenie pre jednu osobu vrátane materiálu, práce a montáže</p>
                </motion.div>
                
                <motion.div 
                  className="bg-gradient-to-br from-gray-50 to-gray-100 p-8 rounded-2xl border-2 border-gray-200 text-center"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">2</span>
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">Dvojhrob + pomník</h4>
                  <div className="text-4xl font-bold text-gray-700 mb-2">od 2400 €</div>
                  <p className="text-gray-800 font-semibold mb-2">vrátane práce</p>
                  <p className="text-sm text-gray-600">Kompletné riešenie pre dve osoby vrátane materiálu, práce a montáže</p>
                </motion.div>
              </div>
              
              <div className="mt-8 text-center">
                <div className="inline-flex items-center bg-blue-50 text-blue-800 px-4 py-2 rounded-full text-sm font-medium">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Finálna cena závisí od materiálu, dizajnu a rozmerov
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-500"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
                layout={false}
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-700"
                    onError={(e) => {
                      console.log(`Failed to load service image: ${service.image}`);
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <div className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center">
                        <Star className="w-5 h-5 text-amber-500 mr-3" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Special buttons for "Jednohroby a Dvojhroby" service */}
                  {service.title === "Jednohroby a Dvojhroby" && (
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-600 mb-4">Zobraziť ukážky:</p>
                      <div className="grid grid-cols-2 gap-3">
                        <motion.button
                          onClick={() => openServiceModal('jednohroby')}
                          className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-300"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Jednohroby
                        </motion.button>
                        <motion.button
                          onClick={() => openServiceModal('dvojhroby')}
                          className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-300"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Dvojhroby
                        </motion.button>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            layout={false}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Naše Práce</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Naša práca je rozdelená do štyroch hlavných kategórií: jednohroby, dvojhroby, gravírovania a hroby s dekoratívnymi pomníkmi. Každá kategória predstavuje naše remeselné zručnosti a dedičstvo tradície.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {portfolioCategories.map((category, index) => (
              <motion.div
                key={index}
                className="group cursor-pointer"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.03 }}
                layout={false}
                onClick={() => openModal(index)}
              >
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 h-full flex flex-col">
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={category.images?.[0]?.src || `/images/${category.folder}-01.webp`} 
                      alt={category.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      onError={(e) => {
                        e.target.src = `/${category.folder}/${category.folder}-01.webp`;
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-white/20 backdrop-blur-md rounded-full p-4">
                        <div className="w-8 h-8 border-2 border-white rounded-full flex items-center justify-center">
                          <div className="w-3 h-3 bg-white rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{category.title}</h3>
                    <p className="text-gray-600 flex-1">{category.description.split('.')[0]}.</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="o-nás" className="py-20 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed opacity-10"
          style={{
            backgroundImage: `url('/pozadia/background-section-about.webp')`
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              layout={false}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">O Nás</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Kamenárstvo Studený SMIX Nitra je rodinná firma s bohatou tradíciou v oblasti kamenárskeho remesla. 
                Špecializujeme sa na výrobu pomníkov a hrobov, brúsenie a gravírovanie kameňa, vždy s dôrazom na kvalitu 
                materiálov a precízne spracovanie.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Naše skúsenosti nám umožňujú poskytovať kamenárske služby na najvyššej úrovni. 
                Každý projekt, či už ide o nový pomník alebo obnovu hrobu, realizujeme s rešpektom a porozumením pre jeho význam.
              </p>
              <div className="grid grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-600 mb-2">20+</div>
                  <div className="text-gray-600">Rokov Skúseností</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-600 mb-2">500+</div>
                  <div className="text-gray-600">Realizovaných Prác</div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              layout={false}
            >
              {/* Image Slider Container */}
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl group">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={aboutSliderIndex}
                    className="absolute inset-0"
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                    layout={false}
                  >
                    <img 
                      src={aboutSliderImages[aboutSliderIndex].src}
                      alt={aboutSliderImages[aboutSliderIndex].alt}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.log(`Failed to load slider image: ${aboutSliderImages[aboutSliderIndex].src}`);
                        // Fallback to a solid background color if image fails
                        e.target.style.display = 'none';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    
                    {/* Image Title Overlay */}
                    <div className="absolute bottom-6 left-6 right-6">
                      <motion.h3 
                        className="text-white text-2xl font-bold text-shadow-lg"
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.4 }}
                        layout={false}
                      >
                        {aboutSliderImages[aboutSliderIndex].title}
                      </motion.h3>
                    </div>
                  </motion.div>
                </AnimatePresence>

                {/* Navigation Dots */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                  {aboutSliderImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === aboutSliderIndex 
                          ? 'bg-amber-500 scale-125' 
                          : 'bg-white/60 hover:bg-white/80'
                      }`}
                    />
                  ))}
                </div>

                {/* Manual Navigation Arrows */}
                <button
                  onClick={goToPreviousSlide}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all duration-200 opacity-0 group-hover:opacity-100"
                >
                  <ChevronLeft size={20} />
                </button>
                
                <button
                  onClick={goToNextSlide}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all duration-200 opacity-0 group-hover:opacity-100"
                >
                  <ChevronRight size={20} />
                </button>

                {/* Progress Bar */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-black/20">
                  <motion.div
                    className="h-full bg-amber-500"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    key={aboutSliderIndex}
                    transition={{ duration: 4, ease: "linear" }}
                  />
                </div>
              </div>

              {/* Slider Info */}
              <div className="mt-6 text-center">
                <p className="text-gray-600">
                  {aboutSliderIndex + 1} / {aboutSliderImages.length} - {aboutSliderImages[aboutSliderIndex].title}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="kontakt" className="py-20 bg-gray-900 text-white relative">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('/pozadia/background-section-contact.webp')`
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            layout={false}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Kontaktujte Nás</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Sme tu pre vás. Kontaktujte nás pre konzultáciu alebo cenovú ponuku.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              layout={false}
            >
              <h3 className="text-2xl font-bold mb-8">Kontaktné Informácie</h3>
              <div className="space-y-6">
                <motion.div 
                  className="flex items-center space-x-4"
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="bg-amber-600 p-3 rounded-full">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-semibold">Telefón</div>
                    <a href="tel:0952263708" className="text-amber-400 hover:text-amber-300">
                      0952 263 708
                    </a>
                  </div>
                </motion.div>

                <motion.div 
                  className="flex items-center space-x-4"
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="bg-amber-600 p-3 rounded-full">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-semibold">Adresa</div>
                    <div className="text-gray-300">Hornočermánska 49, Nitra</div>
                  </div>
                </motion.div>

                <motion.div 
                  className="flex items-center space-x-4"
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="bg-amber-600 p-3 rounded-full">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-semibold">Email</div>
                    <div className="text-gray-300">juro.cuco@gmail.com</div>
                  </div>
                </motion.div>
              </div>

              <div className="mt-12">
                <h4 className="text-xl font-bold mb-4">Pracovné Hodiny</h4>
                <div className="space-y-2 text-gray-300">
                  <div className="flex justify-between">
                    <span>Pondelok - Piatok:</span>
                    <span>8:00 - 16:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sobota:</span>
                    <span>8:00 - 12:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Nedeľa:</span>
                    <span>Zatvorené</span>
                  </div>
                </div>
              </div>

              <div className="mt-12">
                <h4 className="text-xl font-bold mb-4">Základné Ceny</h4>
                <div className="space-y-3 text-gray-300">
                  <div className="flex justify-between items-center">
                    <span>Jednohrob + pomník:</span>
                    <span className="text-amber-400 font-semibold">od 1600 €</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Dvojhrob + pomník:</span>
                    <span className="text-amber-400 font-semibold">od 2400 €</span>
                  </div>
                  <div className="text-sm text-gray-400 mt-2">
                    * Vrátane práce. Presná cenová ponuka na vyžiadanie.
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-md rounded-2xl p-8"
            >
              <h3 className="text-2xl font-bold mb-6">Napíšte Nám</h3>
              <form onSubmit={submitContactForm} className="space-y-6">
                {/* Honeypot field - hidden from users */}
                <input
                  type="text"
                  name="honeypot"
                  value={contactForm.honeypot}
                  onChange={handleContactFormChange}
                  style={{
                    position: 'absolute',
                    left: '-9999px',
                    width: '1px',
                    height: '1px',
                    opacity: 0,
                    pointerEvents: 'none'
                  }}
                  tabIndex={-1}
                  autoComplete="off"
                />
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Meno a Priezvisko <span className="text-amber-400">*</span>
                  </label>
                  <input 
                    type="text" 
                    name="name"
                    value={contactForm.name}
                    onChange={handleContactFormChange}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:border-amber-500 focus:outline-none transition-colors disabled:opacity-50"
                    placeholder="Vaše meno"
                    disabled={isSubmitting}
                    required
                    maxLength={100}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Email <span className="text-amber-400">*</span>
                  </label>
                  <input 
                    type="email" 
                    name="email"
                    value={contactForm.email}
                    onChange={handleContactFormChange}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:border-amber-500 focus:outline-none transition-colors disabled:opacity-50"
                    placeholder="váš.email@example.com"
                    disabled={isSubmitting}
                    required
                    maxLength={200}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Telefón</label>
                  <input 
                    type="tel" 
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleContactFormChange}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:border-amber-500 focus:outline-none transition-colors disabled:opacity-50"
                    placeholder="Váš telefón (voliteľné)"
                    disabled={isSubmitting}
                    maxLength={20}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Správa <span className="text-amber-400">*</span>
                  </label>
                  <textarea 
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactFormChange}
                    rows="4" 
                    className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:border-amber-500 focus:outline-none transition-colors resize-none disabled:opacity-50"
                    placeholder="Opíšte vašu požiadavku..."
                    disabled={isSubmitting}
                    required
                    maxLength={1000}
                  />
                  <div className="text-xs text-gray-400 mt-1">
                    {contactForm.message.length}/1000 znakov
                  </div>
                </div>
                
                {/* Status Messages */}
                <AnimatePresence>
                  {submitStatus && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className={`flex items-center gap-2 p-4 rounded-lg ${
                        submitStatus === 'success' 
                          ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                          : 'bg-red-500/20 text-red-300 border border-red-500/30'
                      }`}
                    >
                      {submitStatus === 'success' ? (
                        <MemoizedCheckCircle size={20} />
                      ) : (
                        <MemoizedAlertCircle size={20} />
                      )}
                      <span>{submitMessage}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                <motion.button 
                  type="submit"
                  className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                    isSubmitting 
                      ? 'bg-gray-600 cursor-not-allowed' 
                      : 'bg-amber-600 hover:bg-amber-700 hover:scale-105'
                  } text-white`}
                  whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                  whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Odosielam...
                    </>
                  ) : (
                    <>
                      <MemoizedSend size={20} />
                      Odoslať Správu
                    </>
                  )}
                </motion.button>
                
                <div className="text-xs text-gray-400 text-center">
                  Ochrana proti spamu je aktívna. Maximum 3 správy za 5 minút.
                </div>
              </form>
            </motion.div>
          </div>

          {/* Google Map Section */}
          <motion.div
            className="mt-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold mb-8 text-center">Naša Poloha</h3>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 shadow-2xl">
              <div className="rounded-xl overflow-hidden">
                <iframe
                  src="https://maps.google.com/maps?q=Hornočermánska+49,+Nitra,+Slovakia&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Kamenárstvo Studený Smix - Hornočermánska 49, Nitra"
                  className="w-full h-96 md:h-80"
                ></iframe>
              </div>
              <div className="mt-4 text-center">
                <p className="text-gray-300">📍 Hornočermánska 49, 949 01 Nitra</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">Kamenárstvo Studený Smix</h3>
              <p className="text-gray-400 mb-4">
                Profesionálne kamenárske služby s tradíciou a kvalitou.
              </p>
              <p className="text-gray-400">
                Hornočermánska 49, Nitra<br />
                Tel: 0952 263 708
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Naše Služby</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Jednohroby a Dvojhroby</li>
                <li>Brúsenie a Čistenie Hrobov</li>
                <li>Obnova Písma - Gravírovanie</li>
                <li>Vyrovnávanie Klesnutých Hrobov</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Pracovné Hodiny</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Po - Pi: 8:00 - 16:00</li>
                <li>So: 8:00 - 12:00</li>
                <li>Ne: Zatvorené</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Kamenárstvo Studený Smix. Všetky práva vyhradené.</p>
          </div>
        </div>
      </footer>

      {/* Service Modal Gallery */}
      <AnimatePresence>
        {isServiceModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            layout={false}
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/90 backdrop-blur-sm cursor-pointer"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  closeServiceModal();
                }
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            {/* Modal Content */}
            <motion.div
              className="relative z-10 w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.2, type: "spring", damping: 20 }}
              layout={false}
            >
              {/* Close Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeServiceModal();
                }}
                className="absolute top-4 right-4 z-30 text-white hover:text-amber-400 transition-colors duration-200 bg-black/70 hover:bg-black/90 rounded-full p-3 md:p-3 sm:p-4 backdrop-blur-sm shadow-lg border border-white/20 touch-manipulation"
                title="Zavrieť"
                style={{ minWidth: '48px', minHeight: '48px' }} // Ensures minimum touch target size
              >
                <X size={20} />
              </button>
              

              {/* Main Image Container */}
              <div className="relative bg-black rounded-lg overflow-hidden shadow-2xl">
                <div className="relative h-screen max-h-[85vh] overflow-hidden">
                  <motion.img
                    key={currentServiceImageIndex}
                    src={serviceModalImages[currentServiceType]?.[currentServiceImageIndex]?.src}
                    alt={serviceModalImages[currentServiceType]?.[currentServiceImageIndex]?.alt}
                    className="w-full h-full object-contain cursor-zoom-in"
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2 }}
                    layout={false}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Toggle fullscreen-like experience
                      if (e.target.style.objectFit === 'cover') {
                        e.target.style.objectFit = 'contain';
                        e.target.parentElement.style.backgroundColor = 'black';
                      } else {
                        e.target.style.objectFit = 'contain';
                        e.target.parentElement.style.backgroundColor = 'black';
                      }
                    }}
                  />
                  
                  {/* Navigation Arrows */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      goToPreviousServiceImage();
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-4 transition-all duration-200 hover:scale-110 z-20 shadow-lg backdrop-blur-sm"
                    title="Predchádzajúci obrázok"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      goToNextServiceImage();
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-4 transition-all duration-200 hover:scale-110 z-20 shadow-lg backdrop-blur-sm"
                    title="Ďalší obrázok"
                  >
                    <ChevronRight size={20} />
                  </button>

                  {/* Image Counter */}
                  <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                    {currentServiceImageIndex + 1} / {serviceModalImages[currentServiceType]?.length || 0}
                  </div>

                  {/* Fullscreen hint */}
                  <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-xs opacity-75">
                    Klikni na obrázok pre plné zobrazenie
                  </div>
                </div>
              </div>

              {/* Thumbnail Navigation */}
              <div className="flex justify-center mt-4 space-x-2 overflow-x-auto pb-2">
                {serviceModalImages[currentServiceType]?.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentServiceImageIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                      index === currentServiceImageIndex
                        ? 'border-amber-500 scale-110'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>

              {/* Keyboard Instructions */}
              <div className="text-center text-gray-400 text-sm mt-4">
                <span className="hidden md:inline">Šípky ← → pre navigáciu • ESC pre zatvorenie • </span>
                <span>Klik na obrázok pre plné zobrazenie</span>
                <span className="md:hidden block mt-1 text-xs">Dotyk na pozadie alebo X tlačidlo na zatvorenie</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Gallery */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            layout={false}
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/90 backdrop-blur-sm cursor-pointer"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  closeModal();
                }
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            {/* Modal Content */}
            <motion.div
              className="relative z-10 w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.2, type: "spring", damping: 20 }}
              layout={false}
            >
              {/* Close Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeModal();
                }}
                className="absolute top-4 right-4 z-30 text-white hover:text-amber-400 transition-colors duration-200 bg-black/70 hover:bg-black/90 rounded-full p-3 md:p-3 sm:p-4 backdrop-blur-sm shadow-lg border border-white/20 touch-manipulation"
                title="Zavrieť"
                style={{ minWidth: '48px', minHeight: '48px' }} // Ensures minimum touch target size
              >
                <X size={20} />
              </button>

              {/* Main Image Container */}
              <div className="relative bg-black rounded-lg overflow-hidden shadow-2xl">
                <div className="relative h-screen max-h-[85vh] overflow-hidden">
                  <motion.img
                    key={currentImageIndex}
                    src={currentCategoryImages[currentImageIndex]?.src}
                    alt={currentCategoryImages[currentImageIndex]?.alt}
                    className="w-full h-full object-contain cursor-zoom-in"
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2 }}
                    layout={false}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Toggle fullscreen-like experience
                      if (e.target.style.objectFit === 'cover') {
                        e.target.style.objectFit = 'contain';
                        e.target.parentElement.style.backgroundColor = 'black';
                      } else {
                        e.target.style.objectFit = 'contain';
                        e.target.parentElement.style.backgroundColor = 'black';
                      }
                    }}
                  />
                  
                  {/* Navigation Arrows */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      goToPrevious();
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-4 transition-all duration-200 hover:scale-110 z-20 shadow-lg backdrop-blur-sm"
                    title="Predchádzajúci obrázok"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      goToNext();
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-4 transition-all duration-200 hover:scale-110 z-20 shadow-lg backdrop-blur-sm"
                    title="Ďalší obrázok"
                  >
                    <ChevronRight size={20} />
                  </button>

                  {/* Image Counter */}
                  <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                    {currentImageIndex + 1} / {currentCategoryImages.length}
                  </div>

                  {/* Fullscreen hint */}
                  <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-xs opacity-75">
                    Klikni na obrázok pre plné zobrazenie
                  </div>
                </div>
              </div>

              {/* Thumbnail Navigation */}
              <div className="flex justify-center mt-4 space-x-2 overflow-x-auto pb-2">
                {currentCategoryImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                      index === currentImageIndex
                        ? 'border-amber-500 scale-110'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>

              {/* Keyboard Instructions */}
              <div className="text-center text-gray-400 text-sm mt-4">
                <span className="hidden md:inline">Šípky ← → pre navigáciu • ESC pre zatvorenie • </span>
                <span>Klik na obrázok pre plné zobrazenie</span>
                <span className="md:hidden block mt-1 text-xs">Dotyk na pozadie alebo X tlačidlo na zatvorenie</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;